import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

export interface SmartTableColumn {
  key: string;
  label: string;
  width?: number; // Column width in pixels
  color?: string; // Column background color
  textColor?: string; // Column text color
  render?: (value: any, row: any, isFooter?: boolean) => React.ReactNode; // Custom render function
}

export interface SmartTableProps<T> {
  data: T[];
  columns: SmartTableColumn[];
  fetchData: () => void;
  settingsKey: string; // unique key for saving settings per table/user
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onAdd?: () => void;
  editable?: boolean;
  addable?: boolean;
  rowKey?: keyof T;
  analyzeButton?: { label: string; onClick: () => void };
  showColumnSearch?: boolean;
  showColumnSettings?: boolean;
  footerData?: any;
}

function getCurrentUsername() {
  return localStorage.getItem('username') || '';
}

export function SmartTable<T extends { [key: string]: any }>({
  data,
  columns: defaultColumns,
  fetchData,
  settingsKey,
  onEdit,
  onDelete,
  onAdd,
  editable = true,
  addable = true,
  rowKey = 'Oid',
  analyzeButton,
  showColumnSearch: externalShowColumnSearch,
  showColumnSettings: externalShowColumnSettings,
  footerData,
}: SmartTableProps<T>) {
  // Column state
  const [columns, setColumns] = useState<SmartTableColumn[]>(defaultColumns);
  const [columnVisibility, setColumnVisibility] = useState<{ [key: string]: boolean }>(() => {
    return defaultColumns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {});
  });
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>(() => {
    return defaultColumns.reduce((acc, col) => ({ ...acc, [col.key]: col.width || 150 }), {});
  });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeColumn, setResizeColumn] = useState<string | null>(null);

  // Date format function
  const formatDate = (value: any, columnKey: string) => {
    if (!value || value === '-') return value;
    
    // Date columns-д date format хийх
    if (columnKey === 'Contract_Time' || columnKey === 'ContractEndDate' || 
        columnKey === 'StartDate' || columnKey === 'EndDate' || 
        columnKey === 'CreatedDate' || columnKey === 'UpdatedDate' ||
        columnKey.includes('Date') || columnKey.includes('Time')) {
      try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        
        // YYYY-MM-DD format
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
      } catch (error) {
        return value;
      }
    }
    
    return value;
  };

  // Mongolian translation function
  const translateToMongolian = (value: any, columnKey: string) => {
    if (!value || value === '-') return value;
    
    // Contract_Time column-д монгол хэлний орчуулга
    if (columnKey === 'Contract_Time') {
      const translations: { [key: string]: string } = {
        'monthly': 'сар',
        'quarterly': 'улирал', 
        'yearly': 'жил',
        'weekly': 'долоо хоног',
        'daily': 'өдөр'
      };
      
      return translations[value.toLowerCase()] || value;
    }
    
    return value;
  };

  // Number format function
  const formatNumber = (value: any, columnKey: string) => {
    if (!value || value === '-') return value;
    
    // Amount columns-д number format хийх
    if (columnKey === 'ContractAmount' || columnKey === 'Amount' || columnKey === 'Price' || 
        columnKey === 'Total' || columnKey === 'Sum' || columnKey.includes('Amount') || 
        columnKey.includes('Price') || columnKey.includes('Total')) {
      try {
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        
        // #,### format (.00 харуулахгүй)
        return num.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        });
      } catch (error) {
        return value;
      }
    }
    
    return value;
  };

  // Conditional styling function
  const getCellStyle = (value: any, columnKey: string) => {
    const baseStyle = {};
    
    // Status columns-д conditional styling
    if (columnKey === 'ReportStatus' || columnKey === 'Status' || columnKey.includes('Status')) {
      if (value === 'Илгээсэн' || value === 'Sent' || value === 'SUBMITTED') {
        return {
          ...baseStyle,
          color: '#16a34a', // Green color
          fontWeight: 'bold'
        };
      } else if (value === 'ШИНЭ' || value === 'NEW' || value === 'DRAFT') {
        return {
          ...baseStyle,
          color: '#dc2626', // Red color
          fontWeight: 'bold'
        };
      }
    }
    
    return baseStyle;
  };
  const [showColumnSettings, setShowColumnSettings] = useState(externalShowColumnSettings || false);
  const [showColumnSearch, setShowColumnSearch] = useState(externalShowColumnSearch || false);
  
  // Sync external props with internal state
  useEffect(() => {
    console.log('🔥 SmartTable: externalShowColumnSearch changed:', externalShowColumnSearch);
    if (externalShowColumnSearch !== undefined) {
      setShowColumnSearch(externalShowColumnSearch);
      console.log('🔥 SmartTable: Updated internal showColumnSearch to:', externalShowColumnSearch);
    }
  }, [externalShowColumnSearch]);
  
  useEffect(() => {
    console.log('🔥 SmartTable: externalShowColumnSettings changed:', externalShowColumnSettings);
    if (externalShowColumnSettings !== undefined) {
      setShowColumnSettings(externalShowColumnSettings);
      console.log('🔥 SmartTable: Updated internal showColumnSettings to:', externalShowColumnSettings);
    }
  }, [externalShowColumnSettings]);
  
  // Debug effect to log render state
  useEffect(() => {
    console.log('🔥 SmartTable: Component render state:', {
      showColumnSearch,
      showColumnSettings,
      externalShowColumnSearch,
      externalShowColumnSettings
    });
  }, [showColumnSearch, showColumnSettings, externalShowColumnSearch, externalShowColumnSettings]);

  const [columnSearch, setColumnSearch] = useState<{ [key: string]: string }>({});
  const [sortKey, setSortKey] = useState(defaultColumns[0]?.key || '');
  const [sortAsc, setSortAsc] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [copiedCell, setCopiedCell] = useState<{ row: string; col: string } | null>(null);
  // Table size state
  const [tableSize, setTableSize] = useState({ width: '100%', height: '800px' });
  // Drag state
  const [draggedHeaderKey, setDraggedHeaderKey] = useState<string | null>(null);
  const [dragOverHeaderIndex, setDragOverHeaderIndex] = useState<number | null>(null);
  const [dragStartPosition, setDragStartPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDraggingHeader, setIsDraggingHeader] = useState(false);
  const [dragCurrentPosition, setDragCurrentPosition] = useState<{ x: number; y: number } | null>(null);
  // Settings window drag
  const [windowPosition, setWindowPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  // Remove willDeleteColumn and all vertical-drag logic
  // Add highlight state for trash zone
  const [isTrashZoneActive, setIsTrashZoneActive] = useState(false);
  // Remove trash zone state and handlers
  // Add vertical drag-to-hide logic with floating indicator
  const [showDeleteHint, setShowDeleteHint] = useState(false);
  const [shouldHideColumn, setShouldHideColumn] = useState(false);
  const [forceEditable, setForceEditable] = useState(false);
  
  // Auto-responsive table size state
  const [autoSize, setAutoSize] = useState(true);
  const [screenSize, setScreenSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  // Load user column settings from database
  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        console.log('🔍 Хэрэглэгчийн багана тохиргоог ачаалж байна...', settingsKey);
        const result = await api.getColumnSettings(settingsKey);
        
        if (result.success && result.data) {
          const { columnOrder, columnVisibility, columnWidths } = result.data;
          
          if (columnOrder && Array.isArray(columnOrder)) {
            // Reorder columns based on saved order
            const orderedColumns = columnOrder
              .map(key => defaultColumns.find(col => col.key === key))
              .filter(Boolean) as SmartTableColumn[];
            
            // Add any new columns that weren't in the saved order
            const newColumns = defaultColumns.filter(col => !columnOrder.includes(col.key));
            const finalColumns = [...orderedColumns, ...newColumns];
            
            setColumns(finalColumns);
            console.log('✅ Багана эрэмбэ ачаалагдлаа:', finalColumns.map(c => c.key));
          }
          
          if (columnVisibility && typeof columnVisibility === 'object') {
            setColumnVisibility(columnVisibility);
            console.log('✅ Багана харагдах тохиргоо ачаалагдлаа:', columnVisibility);
          }
          
          if (columnWidths && typeof columnWidths === 'object') {
            setColumnWidths(columnWidths);
            console.log('✅ Багана өргөн тохиргоо ачаалагдлаа:', columnWidths);
          }
        }
      } catch (error) {
        console.error('❌ Хэрэглэгчийн тохиргоо ачаалахад алдаа:', error);
      }
    };

    loadUserSettings();
  }, [settingsKey, defaultColumns]);

  // Auto-detect screen size and adjust table size
  useEffect(() => {
    const handleResize = () => {
      const newSize = { width: window.innerWidth, height: window.innerHeight };
      setScreenSize(newSize);
      
      if (autoSize) {
        // Calculate optimal table size based on screen size
        const optimalSize = calculateOptimalTableSize(newSize);
        setTableSize(optimalSize);
      }
    };

    window.addEventListener('resize', handleResize);
    // Initial calculation
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [autoSize]);

  // Column resize functions
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  const handleColumnMouseDown = useCallback((e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const currentWidth = columnWidths[columnKey] || 150;
    console.log('🔄 Column resize started:', { columnKey, currentWidth, clientX: e.clientX });
    setResizeStartX(e.clientX);
    setResizeStartWidth(currentWidth);
    setIsResizing(true);
    setResizeColumn(columnKey);
  }, [columnWidths]);

  const handleColumnMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !resizeColumn) return;
    
    const deltaX = e.clientX - resizeStartX;
    const newWidth = Math.max(50, Math.min(500, resizeStartWidth + deltaX));
    
    // console.log('🔄 Column resizing:', { 
    //   resizeColumn, 
    //   deltaX, 
    //   startWidth: resizeStartWidth, 
    //   newWidth,
    //   clientX: e.clientX,
    //   startX: resizeStartX
    // });
    
    setColumnWidths(prev => ({
      ...prev,
      [resizeColumn]: newWidth
    }));
  }, [isResizing, resizeColumn, resizeStartX, resizeStartWidth]);

  // Save user column settings to database
  const saveUserSettings = useCallback(async (newColumns: SmartTableColumn[], newVisibility: { [key: string]: boolean }, newWidths?: { [key: string]: number }) => {
    try {
      console.log('💾 Хэрэглэгчийн багана тохиргоог хадгалаж байна...', settingsKey);
      
      const columnOrder = newColumns.map(col => col.key);
      const columnVisibility = newVisibility;
      const columnWidthsToSave = newWidths || columnWidths;
      
      const result = await api.saveColumnSettings(settingsKey, {
        columnOrder,
        columnVisibility,
        columnWidths: columnWidthsToSave
      });
      
      if (result.success) {
        console.log('✅ Багана тохиргоо амжилттай хадгалагдлаа');
      } else {
        console.error('❌ Багана тохиргоо хадгалахад алдаа:', result.message);
      }
    } catch (error) {
      console.error('❌ Багана тохиргоо хадгалахад алдаа:', error);
    }
  }, [settingsKey, columnWidths]);

  const handleColumnMouseUp = useCallback(() => {
    if (isResizing && resizeColumn) {
      setIsResizing(false);
      setResizeColumn(null);
      // Save column widths
      saveUserSettings(columns, columnVisibility, columnWidths);
    }
  }, [isResizing, resizeColumn, saveUserSettings, columns, columnVisibility, columnWidths]);

  // Add mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleColumnMouseMove);
      document.addEventListener('mouseup', handleColumnMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleColumnMouseMove);
        document.removeEventListener('mouseup', handleColumnMouseUp);
      };
    }
  }, [isResizing, handleColumnMouseMove, handleColumnMouseUp]);

  // Calculate optimal table size based on screen dimensions
  const calculateOptimalTableSize = (screen: { width: number; height: number }) => {
    const { width: screenWidth, height: screenHeight } = screen;
    
    // Leave space for header, buttons, and padding (approximately 200px)
    const availableHeight = screenHeight - 200;
    const availableWidth = screenWidth - 40; // 20px margin on each side
    
    // Determine size category based on screen dimensions
    if (screenWidth < 768) {
      // Mobile: Full width, smaller height
      return {
        width: '100%',
        height: `${Math.max(300, Math.min(400, availableHeight))}px`
      };
    } else if (screenWidth < 1024) {
      // Tablet: Medium size
      return {
        width: '100%',
        height: `${Math.max(400, Math.min(500, availableHeight))}px`
      };
    } else if (screenWidth < 1440) {
      // Desktop: Large size
      return {
        width: '100%',
        height: `${Math.max(500, Math.min(600, availableHeight))}px`
      };
    } else {
      // Large desktop: Maximum size
      return {
        width: '100%',
        height: `${Math.max(600, Math.min(700, availableHeight))}px`
      };
    }
  };

  // If analyzeButton is provided, wrap its onClick to enable edit mode
  const handleAnalyzeClick = () => {
    setForceEditable(true);
    analyzeButton?.onClick && analyzeButton.onClick();
  };

  const handleFinishEdit = () => {
    setForceEditable(false);
  };

  // Save/load settings (updated to use database)
  const saveColumnSettings = async (newColumns: any[], newVisibility: any) => {
    try {
      // Save to database
      await saveUserSettings(newColumns, newVisibility);
    } catch (error) {
      console.error('❌ Багана тохиргоо хадгалахад алдаа:', error);
    }
  };
  // Ensure columns always contains all columns in user order
  useEffect(() => {
    const missing = defaultColumns.filter(dc => !columns.some(c => c.key === dc.key));
    if (missing.length > 0) setColumns([...columns, ...missing]);
    // eslint-disable-next-line
  }, []);
  // Filtering, sorting, pagination
  const visibleColumns = columns.filter(col => columnVisibility[col.key]);
  const filteredData = data.filter((row) => {
    return visibleColumns.every(col => {
      const searchValue = (columnSearch[col.key] || '').toLowerCase();
      if (!searchValue) return true;
      const cellValue = (row[col.key] || '').toString().toLowerCase();
      return cellValue.includes(searchValue);
    });
  });
  const sortedData = [...filteredData].sort((a, b) => {
    const aVal = (a[sortKey] || '').toString().toLowerCase();
    const bVal = (b[sortKey] || '').toString().toLowerCase();
    if (aVal < bVal) return sortAsc ? -1 : 1;
    if (aVal > bVal) return sortAsc ? 1 : -1;
    return 0;
  });
  // Column visibility
  const toggleColumnVisibility = (columnKey: string) => {
    const newVisibility = { ...columnVisibility, [columnKey]: !columnVisibility[columnKey] };
    setColumnVisibility(newVisibility);
    saveColumnSettings(columns, newVisibility);
  };
  // Column reordering
  const moveColumnUp = (index: number) => {
    if (index > 0) {
      const newColumns = [...columns];
      const temp = newColumns[index];
      newColumns[index] = newColumns[index - 1];
      newColumns[index - 1] = temp;
      setColumns(newColumns);
      saveColumnSettings(newColumns, columnVisibility);
    }
  };
  const moveColumnDown = (index: number) => {
    if (index < columns.length - 1) {
      const newColumns = [...columns];
      const temp = newColumns[index];
      newColumns[index] = newColumns[index + 1];
      newColumns[index + 1] = temp;
      setColumns(newColumns);
      saveColumnSettings(newColumns, columnVisibility);
    }
  };
  // Drag-and-drop for headers
  const handleHeaderDragStart = (colKey: string, e: React.DragEvent) => {
    setDraggedHeaderKey(colKey);
    setDragStartPosition({ x: e.clientX, y: e.clientY });
    setIsDraggingHeader(true);
    setShowDeleteHint(false);
    setShouldHideColumn(false);
    // Add global dragend listener
    window.addEventListener('dragend', handleGlobalDragEnd);
  };
  const handleHeaderDrag = (e: React.DragEvent) => {
    if (!dragStartPosition) return;
    setDragCurrentPosition({ x: e.clientX, y: e.clientY });
    const deltaX = Math.abs(e.clientX - dragStartPosition.x);
    const deltaY = Math.abs(e.clientY - dragStartPosition.y);
    const shouldHide = deltaY > 40 && deltaY > deltaX;
    setShowDeleteHint(shouldHide);
    setShouldHideColumn(shouldHide);
  };
  const handleHeaderDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverHeaderIndex(index);
  };
  const handleHeaderDrop = (index: number, e: React.DragEvent) => {
    if (draggedHeaderKey === null || !dragStartPosition) {
      cleanupDragState();
      return;
    }
    // Only reorder columns if not hiding
    if (!shouldHideColumn) {
      const fromIdx = columns.findIndex(col => col.key === draggedHeaderKey);
      if (fromIdx !== -1 && fromIdx !== index) {
        const newColumns = [...columns];
        const [removed] = newColumns.splice(fromIdx, 1);
        newColumns.splice(index, 0, removed);
        setColumns(newColumns);
        saveColumnSettings(newColumns, columnVisibility);
      }
    }
    cleanupDragState();
  };
  const handleHeaderDragEnd = () => {
    handleGlobalDragEnd();
  };
  // Cell copy
  const handleCellCopy = (rowId: string, colKey: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedCell({ row: rowId, col: colKey });
    setTimeout(() => setCopiedCell(null), 700);
  };
  // Settings window drag
  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const isDragHandle = target.closest('[data-drag-handle]');
    if (!isDragHandle) return;
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };
  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setWindowPosition({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
    }
  };
  const handleMouseUp = () => { setIsDragging(false); };
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);
  // Add a handler for dropping on the trash zone
  const handleTrashDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsTrashZoneActive(true);
  };
  const handleTrashDragLeave = (e: React.DragEvent) => {
    setIsTrashZoneActive(false);
  };
  const handleTrashDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedHeaderKey) {
      const newVisibility = { ...columnVisibility, [draggedHeaderKey]: false };
      setColumnVisibility(newVisibility);
      saveColumnSettings(columns, newVisibility);
    }
    setDraggedHeaderKey(null);
    setDragOverHeaderIndex(null);
    setDragStartPosition(null);
    setIsDraggingHeader(false);
    setShowDeleteHint(false);
  };
  // Add these helper functions above their first use
  const cleanupDragState = () => {
    setDraggedHeaderKey(null);
    setDragOverHeaderIndex(null);
    setDragStartPosition(null);
    setIsDraggingHeader(false);
    setShowDeleteHint(false);
    setShouldHideColumn(false);
  };

  const handleGlobalDragEnd = () => {
    if (draggedHeaderKey && shouldHideColumn) {
      const newVisibility = { ...columnVisibility, [draggedHeaderKey]: false };
      setColumnVisibility(newVisibility);
      saveColumnSettings(columns, newVisibility);
    }
    cleanupDragState();
    // Remove global listener
    window.removeEventListener('dragend', handleGlobalDragEnd);
  };
  // Render
  return (
    <div className="w-full">
      {/* Removed Drag Delete Hint Overlay and all willDeleteColumn logic from render. */}
      {showDeleteHint && dragCurrentPosition && (
        <div className="fixed z-[1000] pointer-events-none" style={{ left: dragCurrentPosition.x + 10, top: dragCurrentPosition.y + 10 }}>
          <div className="bg-red-600 text-white px-3 py-2 rounded-lg shadow-lg text-sm font-medium animate-pulse">🗑️ Release to hide</div>
        </div>
      )}
      <div 
        className="smart-table-container overflow-y-auto relative rounded-xl shadow-xl bg-white border-0" 
        style={{ width: tableSize.width, height: tableSize.height }}
      >
        <table className="w-full text-sm border-collapse">
          <thead className="bg-gradient-to-r from-blue-50 to-blue-100 text-gray-800 sticky top-0 z-10">
            <tr>
              {visibleColumns.map((col, idx) => (
                <th
                  key={col.key}
                  draggable
                  onDragStart={e => handleHeaderDragStart(col.key, e)}
                  onDrag={handleHeaderDrag}
                  onDragOver={e => handleHeaderDragOver(e, idx)}
                  onDrop={e => handleHeaderDrop(idx, e)}
                  onDragEnd={handleHeaderDragEnd}
                  className={`px-2 py-2 cursor-move text-gray-800 sticky top-0 z-10 select-none transition-all duration-200 font-medium border-r border-gray-200 ${draggedHeaderKey === col.key ? 'bg-blue-200 opacity-60' : ''} ${dragOverHeaderIndex === idx ? 'bg-green-100 border-2 border-green-400' : ''}`}
                  onClick={() => setSortKey(col.key)}
                  style={{ 
                    width: columnWidths[col.key] || 150, 
                    position: 'relative',
                    backgroundColor: col.color || 'transparent',
                    color: col.textColor || 'inherit'
                  }}
                >
                  {col.label}
                  <div
                    className="absolute top-0 right-0 w-4 h-full cursor-col-resize hover:bg-blue-400 opacity-60 hover:opacity-100 transition-opacity z-20"
                    onMouseDown={e => handleColumnMouseDown(e, col.key)}
                    style={{ 
                      background: isResizing && resizeColumn === col.key ? '#3B82F6' : 'rgba(59, 130, 246, 0.2)',
                      borderRight: isResizing && resizeColumn === col.key ? '3px solid #3B82F6' : '2px solid rgba(59, 130, 246, 0.4)'
                    }}
                  />
                </th>
              ))}
              {editable || forceEditable ? <th className="px-1 py-2 bg-blue-100 sticky top-0 z-10 border-r border-gray-200" style={{ background: '#DBEAFE' }}>Үйлдэл</th> : null}
            </tr>
            {showColumnSearch && (
              <tr>
                {visibleColumns.map(col => (
                  <td key={col.key} className="px-1 py-1 border-r border-gray-100" style={{ 
                    width: columnWidths[col.key] || 150,
                    backgroundColor: col.color || 'transparent',
                    color: col.textColor || 'inherit'
                  }}>
                    <input
                      type="text"
                      value={columnSearch[col.key] || ''}
                      onChange={e => setColumnSearch(cs => ({ ...cs, [col.key]: e.target.value }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                      placeholder={`Хайх...`}
                    />
                  </td>
                ))}
                {editable || forceEditable ? <td></td> : null}
              </tr>
            )}
          </thead>
          <tbody>
            {sortedData.map((row, idx) => (
              <tr 
                key={`${row[rowKey] as string}-${idx}`} 
                className={`hover:bg-blue-50 transition-colors duration-150 ${onEdit ? 'cursor-pointer' : ''}`}
                onClick={() => {
                  if (onEdit && !editable && !forceEditable) {
                    onEdit(row);
                  }
                }}
              >
                {visibleColumns.map(col => {
                  let value = row[col.key];
                  if (value === undefined || value === null || value === '') value = '-';
                  
                  // Custom render function ашиглах
                  let formattedValue;
                  if (col.render) {
                    formattedValue = col.render(value, row);
                  } else {
                    // Date format хийх
                    formattedValue = formatDate(value, col.key);
                    
                    // Number format хийх
                    formattedValue = formatNumber(formattedValue, col.key);
                    
                    // Монгол хэлний орчуулга хийх
                    formattedValue = translateToMongolian(formattedValue, col.key);
                  }
                  
                  return (
                    <td key={col.key} className="px-2 py-2 border-r border-gray-100" style={{ 
                      width: columnWidths[col.key] || 150,
                      backgroundColor: col.color || 'transparent',
                      color: col.textColor || 'inherit'
                    }}>
                      <span
                        onDoubleClick={e => { e.stopPropagation(); handleCellCopy(row[rowKey] as string, col.key, formattedValue); }}
                        className={`cursor-pointer rounded px-2 py-1 transition-all duration-150 hover:bg-gray-100 ${copiedCell && copiedCell.row === row[rowKey] && copiedCell.col === col.key ? 'bg-green-100 ring-2 ring-green-300' : ''}`}
                        style={getCellStyle(formattedValue, col.key)}
                      >
                        {formattedValue}
                      </span>
                    </td>
                  );
                })}
                {editable || forceEditable ? (
                  <td className="px-2 py-2 text-center">
                    {onEdit && <button className="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-150" onClick={() => onEdit(row)}>✏️ Өөрчлөх</button>}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
          {footerData && (
            <tfoot>
              <tr className="bg-gray-100 border-t-2 border-gray-300">
                {visibleColumns.map(col => {
                  let value = footerData[col.key];
                  if (value === undefined || value === null || value === '') value = '-';
                  
                  // Custom render function ашиглах (footer хувилбар)
                  let formattedValue;
                  if (col.render) {
                    formattedValue = col.render(value, footerData, true);
                  } else {
                    // Date format хийх
                    formattedValue = formatDate(value, col.key);
                    
                    // Number format хийх
                    formattedValue = formatNumber(formattedValue, col.key);
                    
                    // Монгол хэлний орчуулга хийх
                    formattedValue = translateToMongolian(formattedValue, col.key);
                  }
                  
                  return (
                    <td key={col.key} className="px-2 py-2 border-r border-gray-100 font-bold" style={{ 
                      width: columnWidths[col.key] || 150,
                      backgroundColor: col.color || '#f3f4f6',
                      color: col.textColor || 'inherit'
                    }}>
                      <span
                        onClick={e => { e.stopPropagation(); handleCellCopy('footer', col.key, formattedValue); }}
                        className={`cursor-copy rounded px-2 py-1 transition-all duration-150 hover:bg-gray-200 ${copiedCell && copiedCell.row === 'footer' && copiedCell.col === col.key ? 'bg-green-100 ring-2 ring-green-300' : ''}`}
                        style={getCellStyle(formattedValue, col.key)}
                      >
                        {formattedValue}
                      </span>
                    </td>
                  );
                })}
                {editable || forceEditable ? (
                  <td className="px-2 py-2 text-center">
                    {/* Footer дээр edit товч байхгүй */}
                  </td>
                ) : null}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {/* Draggable Column Settings Window */}
      {showColumnSettings && (
        <div
          className="fixed bg-white rounded-lg shadow-2xl border border-gray-200 z-50 column-settings-window"
          style={{ left: windowPosition.x, top: windowPosition.y, width: '280px', cursor: isDragging ? 'grabbing' : 'default' }}
          onMouseDown={handleMouseDown}
        >
          {/* Drag Handle */}
          <div data-drag-handle className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-t-lg cursor-grab active:cursor-grabbing select-none">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">⚙️ Багана тохиргоо</span>
              </div>
              <button className="text-white/80 hover:text-white text-lg transition-colors" onClick={e => { e.stopPropagation(); setShowColumnSettings(false); }}>×</button>
            </div>
          </div>
          {/* Content */}
          <div className="p-4 max-h-64 overflow-y-auto">
            <div className="text-xs text-gray-500 mb-3 text-center">↑↓ Товчлууруудаар баганауудыг дахин эрэмбэлэх</div>
            {/* All Columns */}
            <div className="space-y-2 mb-4">
              {defaultColumns.map((col, index) => {
                const isInCurrentColumns = columns.some(c => c.key === col.key);
                const currentIndex = columns.findIndex(c => c.key === col.key);
                return (
                  <div key={col.key} className={`flex items-center gap-3 p-2 rounded transition-colors ${!isInCurrentColumns ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}>
                    {isInCurrentColumns && currentIndex >= 0 && (
                      <div className="flex flex-col gap-1">
                        <button onClick={() => moveColumnUp(currentIndex)} disabled={currentIndex === 0} className="text-gray-400 hover:text-gray-600 disabled:text-gray-200 text-xs p-1" title="Move up">↑</button>
                        <button onClick={() => moveColumnDown(currentIndex)} disabled={currentIndex === columns.length - 1} className="text-gray-400 hover:text-gray-600 disabled:text-gray-200 text-xs p-1" title="Move down">↓</button>
                      </div>
                    )}
                    <input type="checkbox" id={col.key} checked={columnVisibility[col.key] || false} onChange={e => { e.stopPropagation(); toggleColumnVisibility(col.key); }} className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer" onClick={e => e.stopPropagation()} />
                    <label htmlFor={col.key} className={`flex-1 text-sm cursor-pointer select-none ${!isInCurrentColumns ? 'text-gray-500' : 'text-gray-700'}`}>{col.label}</label>
                  </div>
                );
              })}
            </div>
            <div className="border-t pt-3 mt-3">
              <button className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded text-sm transition-colors" onClick={e => { e.stopPropagation(); const newVisibility = defaultColumns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {}); setColumnVisibility(newVisibility); setColumns(defaultColumns); saveColumnSettings(defaultColumns, newVisibility); }}>Бүгдийг харуулах</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Table size info */}
      {/* <div className="mt-2 text-xs text-gray-500 text-center">
        <div className="flex justify-center items-center gap-4">
          <span>Хэмжээ: {tableSize.width} × {tableSize.height}</span>
          <span>Дэлгэц: {screenSize.width}×{screenSize.height}</span>
        </div>
      </div> */}
    </div>
  );
}

export default SmartTable; 